import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Button } from "@/components/ui/button";
import { useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface TiptapEditorProps {
  content: string;
  onChange: (content: string) => void;
  className?: string;
  showToolbar?: boolean;
}

export interface TiptapEditorRef {
  addContent: (newContent: string) => void;
}

export const TiptapEditor = forwardRef<TiptapEditorRef, TiptapEditorProps>(
  ({ content, onChange, className, showToolbar = false }, ref) => {
    const editor = useEditor({
      extensions: [StarterKit],
      content,
      onUpdate: ({ editor }) => {
        console.log("editor.getHTML()", editor.getHTML());
        onChange(editor.getHTML());
      },
      editorProps: {
        attributes: {
          class: "prose prose-invert max-w-none focus:outline-none",
        },
      },
    });

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        addContent: (newContent: string) => {
          console.log("newContent", newContent);
          if (!editor) return;

          // Position cursor at the end
          editor.commands.focus("end");

          // Add double linebreak if there's already content
          if (editor.getHTML() && !editor.getHTML().endsWith("<p></p>")) {
            editor.commands.insertContent("\n");
          }

          // Insert the new content directly without paragraph wrapping
          editor.commands.insertContent(newContent);

          // Ensure we trigger the onChange
          onChange(editor.getHTML());
        },
      }),
      [editor, onChange]
    );

    if (!editor) {
      return <div className="animate-pulse bg-muted/50 h-60 rounded-md"></div>;
    }

    return (
      <div className={cn("flex flex-col space-y-2", className)}>
        {showToolbar && (
          <div className="glass-card flex flex-wrap items-center gap-1 p-2 rounded-md mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={cn(
                "h-8 px-2 text-white",
                editor.isActive("bold") ? "bg-white/10" : ""
              )}
              title="Bold"
            >
              <Bold className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={cn(
                "h-8 px-2 text-white",
                editor.isActive("italic") ? "bg-white/10" : ""
              )}
              title="Italic"
            >
              <Italic className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 1 }).run()
              }
              className={cn(
                "h-8 px-2 text-white",
                editor.isActive("heading", { level: 1 }) ? "bg-white/10" : ""
              )}
              title="Heading 1"
            >
              <Heading1 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }
              className={cn(
                "h-8 px-2 text-white",
                editor.isActive("heading", { level: 2 }) ? "bg-white/10" : ""
              )}
              title="Heading 2"
            >
              <Heading2 className="h-4 w-4" />
            </Button>
            <div className="w-px h-6 bg-white/20 mx-1" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={cn(
                "h-8 px-2 text-white",
                editor.isActive("bulletList") ? "bg-white/10" : ""
              )}
              title="Bullet List"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={cn(
                "h-8 px-2 text-white",
                editor.isActive("orderedList") ? "bg-white/10" : ""
              )}
              title="Ordered List"
            >
              <ListOrdered className="h-4 w-4" />
            </Button>
          </div>
        )}
        <div
          className="glass-card bg-card/30 p-4 min-h-[300px] rounded-lg overflow-hidden cursor-text"
          onClick={() => editor.chain().focus().run()}
        >
          <EditorContent editor={editor} className="w-full h-full" />
        </div>
      </div>
    );
  }
);

TiptapEditor.displayName = "TiptapEditor";
